import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIError, api } from '../lib/api';
import { useAuth } from '../lib/auth';

// Login — email + password sign-in / register toggle. Magic-link plumbing is
// still in the API client (api.requestMagicLink) for future password-reset
// flows but isn't surfaced in this UI; the user said no email infra → no
// magic links.
type Mode = 'login' | 'register';

export default function Login() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'register') {
        // Light client-side validation that mirrors the server. Catches
        // typos before paying for a 400 round-trip; keeps the error
        // message specific.
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          return;
        }
      }
      // Both endpoints return { user, session_token }. Pump the user into
      // AuthProvider so RequireAuth on /dashboard sees us as authed —
      // otherwise it'd bounce right back here on the very next render
      // because AuthProvider only auto-refreshes on its own mount.
      const resp =
        mode === 'register'
          ? await api.register({ email: email.trim(), password, name: name.trim() })
          : await api.login({ email: email.trim(), password });
      setAuthed(resp.user);
      // RequireBet on /dashboard will bounce to /onboarding if the user
      // hasn't committed a bet yet, so we always land on /dashboard from
      // here and let the routing handle the rest.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof APIError) {
        const body = err.body as { error?: string } | null;
        // Specific messaging by status — login: always "invalid credentials";
        // register: 409 means "use sign in instead", 400 forwards the
        // server's specific reason (short password, bad email, etc.).
        if (mode === 'login') {
          setError('Invalid email or password');
        } else if (err.status === 409) {
          setError('Email already registered — sign in instead');
        } else {
          setError(body?.error ?? `error ${err.status}`);
        }
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-full max-w-sm mx-auto px-6 py-16 space-y-6">
      {/* The headline sits on the bare canvas; the controls live on a sheet of
          white paper below it — the same object every card in the app is. */}
      <header className="space-y-3">
        <p className="label-eyebrow">Gios Protocol</p>
        <h1 className="font-display text-6xl leading-none">
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </h1>
        <p className="text-sm text-fg-muted leading-relaxed">
          {mode === 'login'
            ? 'Email and password. No magic links, no third-party auth.'
            : 'Register with an email + a password (min 8 chars). One account per email.'}
        </p>
      </header>

      <div className="card p-5 sm:p-6 space-y-5">
        <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-bg-sunken">
          <ModeButton
            active={mode === 'login'}
            onClick={() => {
              setMode('login');
              setError(null);
            }}
          >
            Sign in
          </ModeButton>
          <ModeButton
            active={mode === 'register'}
            onClick={() => {
              setMode('register');
              setError(null);
            }}
          >
            Register
          </ModeButton>
        </div>

        <form onSubmit={submit} className="space-y-4" aria-label={mode}>
          {mode === 'register' && (
            <Field
              label="Name"
              type="text"
              value={name}
              onChange={setName}
              placeholder="Your name"
              autoComplete="name"
              required
            />
          )}
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            autoComplete="email"
            required
            autoFocus
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder={mode === 'register' ? 'min 8 characters' : ''}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            required
          />
          <button
            type="submit"
            disabled={busy || !email || !password || (mode === 'register' && !name)}
            className="btn-primary w-full py-3.5 font-mono uppercase tracking-[0.14em] text-sm"
          >
            {busy
              ? mode === 'register'
                ? 'Creating…'
                : 'Signing in…'
              : mode === 'register'
              ? 'Create account →'
              : 'Sign in →'}
          </button>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs font-semibold py-2.5 rounded-full transition-colors ${
        active ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'
      }`}
    >
      {children}
    </button>
  );
}

// Field is a labeled text input with a value-string callback (rather than
// the raw event). We Omit the native onChange/value from the input attrs
// so the prop signatures don't conflict.
type FieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>;

function Field({ label, value, onChange, ...rest }: FieldProps) {
  return (
    <label className="block space-y-1.5">
      <span className="label-eyebrow">{label}</span>
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-control bg-bg-sunken border border-line px-3 py-3 text-base text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
      />
    </label>
  );
}
